import { FC, ReactElement } from "react";
import { Box, Typography } from "@mui/material";
import ExampleChatMessageCard from "./ExampleChatMessageCard";
import RIStartLogo from "@/components/icons/RIStartLogo";

interface NewChatViewProps {
  userName?: string;
  onExampleClick: (message: string) => void;
}

const NewChatView: FC<NewChatViewProps> = ({ userName = "there", onExampleClick }): ReactElement => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        pt: 0, // Removed padding-top to allow better centering/vertical positioning
        pb: 8, // Add padding-bottom to lift content slightly higher
      }}
    >
      {/* Fluid Wallpaper Background */}
      <Box
        sx={{
          position: "absolute",
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
          width: "600px",
          height: "600px",
          opacity: 0.15,
          zIndex: 0,
          pointerEvents: "none",
          background: "radial-gradient(circle, #0B494B 0%, rgba(255,255,255,0) 70%)",
          filter: "blur(80px)",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 4,
          width: "100%",
          maxWidth: "800px",
          px: 3,
        }}
      >
        {/* Greeting */}
        <Box sx={{ textAlign: "center" }}>
          {/* Logo Orb */}
          <Box sx={{ 
            position: 'relative',
            width: 80, 
            height: 80, 
            borderRadius: '50%', 
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'radial-gradient(circle at 30% 30%, #12686C 0%, #0B2B2C 100%)',
            boxShadow: '0 0 30px rgba(18, 104, 108, 0.5), inset 0 0 20px rgba(255, 255, 255, 0.1)',
            margin: '0 auto 24px auto',
            overflow: 'hidden',
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '10%',
              left: '15%',
              width: '20%',
              height: '10%',
              background: 'rgba(255, 255, 255, 0.3)',
              borderRadius: '50%',
              filter: 'blur(4px)',
              transform: 'rotate(-45deg)',
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-2px',
              borderRadius: '50%',
              padding: '2px',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0))',
              WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
            }
          }}>
             <Box sx={{ width: 37, height: 22, color: 'white', zIndex: 2 }}>
               <RIStartLogo width="100%" height="100%" />
             </Box>
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontFamily: "Inter Display, Inter, sans-serif",
              fontWeight: 600,
              fontSize: "32px",
              color: "#031112",
              letterSpacing: "-0.32px",
              mb: 1,
            }}
          >
            Hey {userName}, how can we help you today?
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: "Inter Display, Inter, sans-serif",
              fontWeight: 500,
              fontSize: "14px",
              color: "#8E9494",
              letterSpacing: "-0.14px",
            }}
          >
            Tell us what you need, and we’ll handle the rest.
          </Typography>
        </Box>

        {/* Example Cards */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 1.5,
            mt: 4,
          }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography
                    sx={{
                    fontFamily: "Inter Display, Inter, sans-serif",
                    fontWeight: 500,
                    fontSize: "12px",
                    color: "#7F8C99",
                    letterSpacing: "-0.12px",
                    }}
                >
                    Get started with an example below
                </Typography>
            </Box>
          
          <Box sx={{ display: 'flex', gap: 2, width: '100%', overflowX: 'auto', pb: 1 }}>
            <ExampleChatMessageCard
              message="Classify a 100% Men's Linen Dress Shirt"
              onClick={onExampleClick}
              style={{ flex: 1, minWidth: '200px' }}
            />
            <ExampleChatMessageCard
              message="Classify Dehusked Whole Organic Coconuts"
              onClick={onExampleClick}
              style={{ flex: 1, minWidth: '200px' }}
            />
            <ExampleChatMessageCard
              message="Classify a Used Electric Car with over 250 miles range"
              onClick={onExampleClick}
              style={{ flex: 1, minWidth: '200px' }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default NewChatView;

